import React, { useMemo, useState } from 'react';
import Header from '../components/Header';
import './LearningHub.css';
import { learningSections } from '../data/learningSections';
import { useNavigate } from 'react-router-dom';

const letterFor = (index) => String.fromCharCode(65 + index);

const getSectionStats = (section, responses) => {
  const sectionResponses = responses[section.id] || {};
  const answered = Object.keys(sectionResponses).length;
  const correct = section.questions.reduce(
    (sum, q) => sum + (sectionResponses[q.id] === q.answerIndex ? 1 : 0),
    0
  );
  return { answered, correct };
};

export default function LearningHub() {
  const navigate = useNavigate();
  const defaultSectionId = learningSections[0]?.id || '';
  const [activeSectionId, setActiveSectionId] = useState(defaultSectionId);
  const [responses, setResponses] = useState({});

  const activeSection = useMemo(
    () => learningSections.find((section) => section.id === activeSectionId) || learningSections[0],
    [activeSectionId]
  );

  const handlePick = (questionId, optionIndex) => {
    if (!activeSection) return;
    setResponses((prev) => ({
      ...prev,
      [activeSection.id]: {
        ...(prev[activeSection.id] || {}),
        [questionId]: optionIndex,
      },
    }));
  };

  const goToNextSection = () => {
    const currentIndex = learningSections.findIndex((s) => s.id === activeSectionId);
    const nextIndex = (currentIndex + 1) % learningSections.length;
    setActiveSectionId(learningSections[nextIndex].id);
  };

  const activeStats = activeSection ? getSectionStats(activeSection, responses) : { answered: 0, correct: 0 };
  const completion = activeSection ? Math.round((activeStats.answered / activeSection.questions.length) * 100) : 0;

  return (
    <div className="learning-shell">
      <Header />
      <div className="learning-inner">
        <div className="learning-hero">
          <div className="hero-copy">
            <h1>Driving Knowledge Tracks</h1>
            <p>Move through focused sections like traffic control, highway flow, and towing safety. Each module has five quick-check questions to lock in the lesson. Build confidence step by step and progress through structured learning paths aligned with Alberta's Driver's Guide.</p>
            <div className="hero-meta">
              <span className="pill">9 sections</span>
              <span className="pill">5 questions each</span>
              <span className="pill">Track progress</span>
            </div>
          </div>
          <div className="hero-card">
            <div className="hero-stat">
              <span>Now learning</span>
              <strong>{activeSection?.title || 'Select a section'}</strong>
            </div>
            <div className="hero-stat">
              <span>Completion</span>
              <strong>{completion}%</strong>
            </div>
            <div className="hero-stat">
              <span>Correct this section</span>
              <strong>{activeStats.correct} / {activeSection?.questions.length || 0}</strong>
            </div>
            <div className="hero-stat">
              <span>All sections</span>
              <strong>{learningSections.length} ready</strong>
            </div>
          </div>
        </div>

        <div className="learning-grid">
          <div className="section-column">
            {learningSections.map((section) => {
              const { answered, correct } = getSectionStats(section, responses);
              return (
                <button
                  key={section.id}
                  className={`section-card ${section.id === activeSectionId ? 'active' : ''}`}
                  onClick={() => setActiveSectionId(section.id)}
                >
                  <div className="section-title">{section.icon} {section.title}</div>
                  <p className="section-summary">{section.summary}</p>
                  <div className="section-meta">
                    <span className="progress">{correct}/{section.questions.length} correct</span>
                    <span className="meta-text">{answered}/{section.questions.length} answered</span>
                  </div>
                </button>
              );
            })}
            <button className="section-card navigation-card" onClick={goToNextSection}>
              <div className="section-title">➡️ Next Section</div>
              <p className="section-summary">Skip ahead when you're ready to move forward.</p>
              <div className="section-meta">
                <span className="progress">Complete all {learningSections.length}</span>
              </div>
            </button>
          </div>

          <div className="questions-column">
            {activeSection && (
              <>
                <div className="section-header">
                  <span className="pill">{activeSection.icon} Section focus</span>
                  <h2>{activeSection.title}</h2>
                  <p>{activeSection.focus}</p>
                </div>
                <div className="section-intro">
                  <p>{activeSection.intro}</p>
                </div>
                <div className="question-list">
                  {activeSection.questions.map((question, idx) => {
                    const response = (responses[activeSection.id] || {})[question.id];
                    const isAnswered = typeof response === 'number';
                    const isCorrect = isAnswered && response === question.answerIndex;
                    return (
                      <div
                        key={question.id}
                        className={`question-card ${isAnswered ? 'answered' : ''} ${isCorrect ? 'correct' : ''} ${isAnswered && !isCorrect ? 'incorrect' : ''}`}
                      >
                        <div className="question-top">
                          <span className="pill">{idx + 1} / {activeSection.questions.length}</span>
                          <span className="meta-text">Tap an option to check understanding</span>
                        </div>
                        <h3>{question.prompt}</h3>
                        <div className="options">
                          {question.options.map((option, optionIndex) => {
                            const picked = response === optionIndex;
                            const classes = [
                              'option-chip',
                              picked ? 'selected' : '',
                              isAnswered && optionIndex === question.answerIndex ? 'right' : '',
                              isAnswered && picked && optionIndex !== question.answerIndex ? 'wrong' : '',
                            ].join(' ').trim();
                            return (
                              <button
                                key={option}
                                className={classes}
                                type="button"
                                onClick={() => handlePick(question.id, optionIndex)}
                              >
                                <span className="letter">{letterFor(optionIndex)}</span>
                                <span>{option}</span>
                              </button>
                            );
                          })}
                        </div>
                        {isAnswered && (
                          <div className="feedback">
                            {isCorrect ? (
                              <strong>✓ Correct.</strong>
                            ) : (
                              <strong>✗ Answer: {question.options[question.answerIndex]}</strong>
                            )}
                            <span> {question.explanation}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="practice-cta">
                  <button 
                    className="cta-practice"
                    onClick={() => navigate('/practice')}
                  >
                    📝 Continue with Practice Tests
                  </button>
                  <p className="cta-note">Ready to test your knowledge? Move to full-length practice tests with detailed feedback.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
